import { z } from "zod";

import { ValueConstraintSchema } from "./valueConstraint.schema";

/**
 * The declared types a card variable can carry.
 *
 * A caller's value is checked against the declared type and REFUSED when it does not
 * match. It is never coerced — a `number` variable does not accept the string "4711",
 * because accepting it would mean the card author's declared shape is advisory.
 */
export const CARD_VARIABLE_TYPES = ["string", "number", "boolean", "object", "array"] as const;
export const CardVariableTypeSchema = z.enum(CARD_VARIABLE_TYPES);
export type CardVariableType = z.infer<typeof CardVariableTypeSchema>;

/**
 * A variable defined on an Access Card.
 *
 * Card variables act as the card's "function parameters" — inputs that consumers
 * must provide when using the card. They are referenced in disabled parameter
 * values using @name syntax (e.g. "FOLLOW UP: @mail_subject").
 *
 * When a workflow node or agent skill uses a card, card variables surface as
 * additional inputs via InputWithVariables, allowing consumers to bind them
 * to workflow variables or static values.
 */
export const CardVariableSchema = z.object({
  /** The @-reference identifier (e.g. "mail_subject"). Lowercase snake_case. */
  name: z
    .string()
    .min(1)
    .refine((v) => /^[a-z][a-z0-9_]*$/.test(v), "Must be lowercase snake_case (e.g. mail_subject)"),
  /** Human-readable label shown to consumers (e.g. "Original Email Subject") */
  title: z.string().min(1),
  /** Data type. Matches workflow variable types for consistency. */
  type: CardVariableTypeSchema.default("string"),
  /** Optional description explaining what this variable is for */
  description: z.string().optional(),
  /** Whether consumers must provide a value for this variable */
  required: z.boolean().optional(),
  /**
   * What a consumer's value for this variable is allowed to be.
   *
   * Checked BEFORE substitution, against the value as it will be rendered into the
   * template. A refusal here stops the smuggled payload one step earlier than the
   * post-substitution check on the parameter does — both layers ship, and neither
   * substitutes for the other.
   */
  constraint: ValueConstraintSchema.optional()
});

export type CardVariable = z.infer<typeof CardVariableSchema>;

export const CardVariablesSchema = z
  .array(CardVariableSchema)
  .refine(
    (vars) => new Set(vars.map((v) => v.name)).size === vars.length,
    "Variable names must be unique"
  );
export type CardVariables = z.infer<typeof CardVariablesSchema>;
